import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, signal, TemplateRef, viewChild } from '@angular/core';
import { FilterChipComponent } from 'src/app/home-page/components/filter-chip/filter-chip.component';
import { SectionTitleComponent } from 'src/app/home-page/components/section-title/section-title.component';
import { Subject, takeUntil } from 'rxjs';
import { ActionButtonComponent } from 'src/app/home-page/components/action-button/action-button.component';
import { CarouselArrowsComponent } from 'src/app/home-page/components/carousel-arrows/carousel-arrows.component';
import { ProductCardComponent } from '@core/components/product-card/product-card.component';
import { CarouselBaseComponent } from 'src/app/home-page/components/carousel-base/carousel-base.component';
import { PreviewCardComponent } from 'src/app/home-page/components/preview-card/preview-card.component';
import { NavigationService } from '@core/services/navigation.service';
import { IPreviewCard } from 'src/app/home-page/components/preview-card/IPreviewClass';
import { PreviewCard } from 'src/app/home-page/components/preview-card/preview-card.class';
import { CarouselItemBase } from 'src/app/home-page/components/carousel-base/carousel-item-base.model';
import { Product } from '@core/models/piramide/Product';
import { FilterChipBaseModel } from 'src/app/home-page/components/filter-chip/filter-chip-base.model';
import { FeaturedBrandsService } from 'src/app/home-page/services/featured-brands.service';
import { FilterChipTypeEnum } from 'src/app/home-page/components/filter-chip/filter-chip-type.enum';
import { SwiperOptions } from 'swiper/types';
import { CAROUSEL_BASE_DEFAULT_CONFIG } from 'src/app/home-page/components/carousel-base/carousel-base.default-config';

type BrandFilterChip = FilterChipBaseModel<Map<number, Product[]>, Product[]>;

@Component({
  selector: 'cgw-brands-section',
  standalone: true,
  imports: [
    SectionTitleComponent,
    CommonModule,
    CarouselArrowsComponent,
    CarouselBaseComponent,
    FilterChipComponent,
    ProductCardComponent,
    PreviewCardComponent,
  ],
  templateUrl: './brands-section.component.html',
  styleUrl: './brands-section.component.scss',
})
export class BrandsSectionComponent implements OnInit, OnDestroy {
  private readonly _productTemplateRef = viewChild('productTemplate', { read: TemplateRef });
  private _previewCards: IPreviewCard[] = [];
  private readonly _destroyS = new Subject<boolean>();

  public readonly carousel =
    viewChild<CarouselBaseComponent<Product, CarouselItemBase<Product>>>(CarouselBaseComponent);
  public readonly carouselConfig = signal<SwiperOptions>({
    ...CAROUSEL_BASE_DEFAULT_CONFIG,
    slidesPerView: 1.5,
    spaceBetween: 10,
    breakpoints: {
      992: {
        slidesPerView: 3,
      },
    },
  });
  public readonly carouselItems = signal<CarouselItemBase<Product>[]>([]);
  public readonly filterChips = signal<BrandFilterChip[]>([]);

  public mainBrand = signal<PreviewCard | null>(null);
  public productsByBrand = new Map<number, Product[]>();

  constructor(
    private navigationService: NavigationService,
    private _featuredBrandsService: FeaturedBrandsService,
  ) {}

  public ngOnInit(): void {
    this._featuredBrandsService.brands$.pipe(takeUntil(this._destroyS)).subscribe((featBrands) => {
      const chips: BrandFilterChip[] = [];

      this._previewCards = [];

      featBrands.forEach((fb, idx) => {
        this._previewCards.push({
          id: fb.id,
          title: fb.brand?.nombre,
          class: 'brand',
          backgroundImages: fb.images,
          onClick: () => {
            if (fb.link) this.navigationService.navigateToUrl(fb.link);
            else
              this.navigationService.navigate(['/productos'], {
                queryParams: {
                  criterio: fb.brand.nombre,
                },
              });
          },
        });

        chips.push(
          new FilterChipBaseModel({
            id: fb.id,
            type: FilterChipTypeEnum.brand,
            title: fb.brand.nombre,
            icon: fb.logoName,
            filterFn: (pm) => {
              this.carousel().slideTo(0);
              return pm.get(fb.id) || [];
            },
            disabled: fb.disabled,
            active: idx == 0,
          }),
        );
      });

      this.productsByBrand = new Map(featBrands.map((fb) => [fb.id, fb.products]));
      this.filterChips.set(chips);

      this.setFilteredProducts(chips[0], chips[0].filterFn(this.productsByBrand));
    });
  }

  public setFilteredProducts(selectedChip: BrandFilterChip, filteredList: Array<Product>): void {
    this.mainBrand.set(
      new PreviewCard(this._previewCards.find((card) => card.id == selectedChip.id)),
    );
    this.carouselItems.set(
      filteredList.map(
        (p) =>
          new CarouselItemBase({
            id: p.id_producto,
            data: p,
            templateRef: this._productTemplateRef(),
            templateContext: { item: p },
          }),
      ),
    );
  }

  public ngOnDestroy(): void {
    this._destroyS.next(true);
    this._destroyS.complete();
  }
}
